import { BaseText, Headline1, Meta, theme } from 'aimo-ui';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';
import {
  CenterFullScreen,
  ColumnWithMargins,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  AdditionalDiscount,
  BillingFrequency,
  BillingType,
  CountryCode,
  Maybe,
  Pmc,
  Promotion,
} from 'types/generatedSchemaTypes';
import {
  currencyFormatter,
  getPriceForSalesOrder,
  isSalesOrderFree,
} from 'utils/commonUtils';
import {
  getDiscountedAmount,
  getPromotionText,
  getPromotionValidityText,
  isPromotionActive,
} from 'utils/promotionUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import AccountContext from 'context/AccountContext.tsx';

const LineThroughText = styled.Text`
  text-decoration-line: line-through;
  text-decoration-style: solid;
`;

type PriceInfoProps = {
  pmc: Maybe<Pmc> | undefined;
  totalPrice: number | undefined;
  useAdditionalDiscount: boolean;
  additionalDiscount: Maybe<AdditionalDiscount> | undefined;
  promotion: Maybe<Promotion> | undefined;
  country: Maybe<CountryCode> | undefined;
  billingFrequency: Maybe<BillingFrequency> | undefined;
  billingType: Maybe<BillingType> | undefined;
  validFrom: Date;
  serviceFee: number;
  isSubscription?: boolean;
};

const SalesOrderPermiPriceInfo = ({
  pmc,
  totalPrice,
  useAdditionalDiscount,
  additionalDiscount,
  promotion,
  country,
  billingFrequency,
  billingType,
  validFrom,
  serviceFee,
}: PriceInfoProps) => {
  const { t } = useTranslation();
  const freeSalesOrder = isSalesOrderFree(pmc?.prices);
  const discountOrPromotion =
    (useAdditionalDiscount && !!additionalDiscount) || !!promotion;
  const {
    customer: { preferredLanguage },
  } = useContext(AccountContext);

  const permitRenewalText = () => {
    if (billingType === 'ONE_TIME') {
      return t('longTermParking.oneTimeFee');
    } else {
      if (freeSalesOrder) {
        return t('cityPermits.renewInfo');
      }
      return t(`longTermParking.recurring.${billingFrequency}`);
    }
  };

  const discountedPrice = (value: number, percent: number) =>
    getDiscountedAmount(totalPrice ?? 0, serviceFee, value, percent);

  const priceWithDiscountOrPromotion = () => {
    if (useAdditionalDiscount && !!additionalDiscount) {
      return discountedPrice(
        additionalDiscount?.value || 0,
        additionalDiscount?.percent || 0
      );
    } else if (promotion) {
      return discountedPrice(promotion?.value || 0, promotion?.percent || 0);
    }
    return 0;
  };

  const lineTotalWithDiscountOrPromotion = () => {
    if (
      useAdditionalDiscount &&
      isPromotionActive(additionalDiscount?.validity, validFrom)
    ) {
      return discountedPrice(
        additionalDiscount?.value || 0,
        additionalDiscount?.percent || 0
      );
    }

    if (isPromotionActive(promotion?.validity, validFrom)) {
      return discountedPrice(promotion?.value || 0, promotion?.percent || 0);
    }
    return totalPrice ?? 0;
  };

  const discountOrPromotionText = () => {
    if (useAdditionalDiscount && additionalDiscount) {
      return getPromotionText(
        additionalDiscount,
        country,
        undefined,
        'permit.discount',
        t
      );
    }

    return getPromotionText(
      promotion,
      country,
      preferredLanguage,
      'permit.promotion',
      t
    );
  };

  const discountPromotionValidityText = () => {
    if (useAdditionalDiscount && additionalDiscount) {
      return getPromotionValidityText(additionalDiscount.validity) ?? ' - ';
    }

    return promotion
      ? (getPromotionValidityText(promotion.validity) ?? ' - ')
      : undefined;
  };

  const priceText = () => {
    if (!country) {
      return '';
    }
    if (totalPrice) {
      return currencyFormatter(lineTotalWithDiscountOrPromotion(), country);
    }
    if (billingFrequency) {
      return getPriceForSalesOrder(
        pmc?.prices,
        country,
        new Date(),
        billingFrequency
      ).totalPrice;
    }
    return '';
  };

  const serviceFeeText = () => {
    if (!country) {
      return '';
    }
    if (serviceFee) {
      return t('price.serviceFee', {
        price: currencyFormatter(serviceFee, country),
      });
    }
    if (billingFrequency) {
      return getPriceForSalesOrder(
        pmc?.prices,
        country,
        new Date(),
        billingFrequency
      ).serviceFee;
    }
    return '';
  };

  return (
    <StyledWrapper center mb={5}>
      <ColumnWithMargins mt={theme.normalising.heightPixel(20)}>
        <CenterFullScreen>
          <Headline1 center {...testIdentifiers('permit-details-price')}>
            {priceText()}
          </Headline1>
          {serviceFeeText() && (
            <ColumnWithMargins mt={5}>
              <Meta color={theme.colors.gray300}>{serviceFeeText()}</Meta>
            </ColumnWithMargins>
          )}
          <ColumnWithMargins mt={5}>
            <Meta
              color={theme.colors.gray300}
              testID="permit-details-type"
              center>
              {permitRenewalText()}
            </Meta>
          </ColumnWithMargins>
        </CenterFullScreen>
      </ColumnWithMargins>
      {!!totalPrice && country && discountOrPromotion && (
        <>
          <StyledWrapper center mt={10} mb={0}>
            <Meta color={theme.colors.gray300} testID="permit-discount-type">
              {discountOrPromotionText()}
            </Meta>
          </StyledWrapper>
          <StyledWrapper center mt={0} mb={0}>
            <Meta color={theme.colors.gray300}>
              {discountPromotionValidityText()}
            </Meta>
          </StyledWrapper>
          <StyledWrapper center mt={0}>
            <Meta>
              {
                <LineThroughText>
                  <BaseText color={theme.colors.gray300}>
                    {currencyFormatter(totalPrice, country)}
                  </BaseText>
                </LineThroughText>
              }
              {
                <BaseText color={theme.colors.black}>
                  {' ' +
                    currencyFormatter(priceWithDiscountOrPromotion(), country)}
                </BaseText>
              }
            </Meta>
          </StyledWrapper>
        </>
      )}
    </StyledWrapper>
  );
};

export default SalesOrderPermiPriceInfo;
